import {
  applyMockScenario,
  createApiClients,
  createCognitoAuth,
  createMockApiClients,
  createMockAuth,
  createPersonalMockWorld,
  type ApiClients,
  type ApiSession,
  type AppConfig,
  type AppPrincipal,
  type AuthAdapter,
  type MockWorld,
} from '@platform/app-kit';
import { PRODUCT, PRODUCT_NAME } from './shell/brand.ts';

/** Everything the app needs that depends on the auth mode, built once from the configuration. */
export interface Runtime {
  config: AppConfig;
  adapter: AuthAdapter;
  createApi: (session: ApiSession & { getPrincipal: () => AppPrincipal | null }) => ApiClients;
  /** Present in mock mode only (the mock payment page completes the checkout in it). */
  world: MockWorld | null;
}

export function createRuntime(config: AppConfig): Runtime {
  // `config.appName` is the cell's product's display name on a real edge; the dev server's
  // default ('Sprocket') must not name this app in an authenticator or anywhere else.
  const appName = config.appName === 'Sprocket' ? PRODUCT_NAME : config.appName;
  if (config.auth.mode === 'mock') {
    const world = createPersonalMockWorld(PRODUCT);
    // End-to-end tests ask for a situation with `?mock=<name>` on the first page load.
    applyMockScenario(world, new URLSearchParams(window.location.search).get('mock'));
    return {
      config,
      world,
      adapter: createMockAuth({ world, audience: 'tenant', appName }),
      createApi: (session) =>
        createMockApiClients({
          world,
          getPrincipal: session.getPrincipal,
          checkoutOrigin: `${window.location.origin}/__mock`,
        }),
    };
  }
  return {
    config,
    world: null,
    // Owner ruling 2026-11-03 (docs/lanes/2-shells.md, owner question 1): tokens in this tab's
    // sessionStorage — cleared when the tab closes, never localStorage — so a person comes back
    // from the payment page still signed in. `/signup/complete` still copes with a lost session.
    adapter: createCognitoAuth({
      config: config.auth,
      audience: 'tenant',
      appName,
      storage: 'session',
    }),
    createApi: (session) => createApiClients({ config, session }),
  };
}
